import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// Lista no padrão de app (celular e tablet, abaixo de 1024px), aprovada no
// canvas "Cards mobile do painel": um bloco arredondado com linhas separadas
// por fio. Cada linha tem um elemento à esquerda (avatar, ícone), título
// forte, uma linha de apoio, metadado opcional e informação curta à direita.
// O desktop continua com a tabela; a tela mostra uma ou outra com
// `lg:hidden` / `hidden lg:block`.

export function ListaMobile({
  children,
  className,
  titulo,
  resumo,
  rodape,
}: {
  children: ReactNode;
  className?: string;
  // Cabeçalho opcional dentro do bloco (ex.: "Pagamentos neste mês").
  titulo?: ReactNode;
  // À direita do título (ex.: total, link "Ver todos").
  resumo?: ReactNode;
  // Linha final (ex.: link "Ver todos os 18").
  rodape?: ReactNode;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-border bg-card shadow-card',
        className
      )}
    >
      {(titulo || resumo) && (
        <div className="flex items-baseline justify-between gap-3 px-4 pb-2 pt-3.5">
          {titulo && <h3 className="text-[15px] font-semibold text-text-title">{titulo}</h3>}
          {resumo}
        </div>
      )}
      <ul
        className={cn(
          'divide-y divide-border/70',
          (titulo || resumo) && 'border-t border-border/70'
        )}
      >
        {children}
      </ul>
      {rodape && <div className="border-t border-border/70 px-4 py-3 text-sm">{rodape}</div>}
    </div>
  );
}

interface ItemListaProps {
  // Avatar, ícone em quadrado etc.
  inicio?: ReactNode;
  titulo: ReactNode;
  // Selo ao lado do título (verificado, Popular...).
  tituloExtra?: ReactNode;
  // Linha acima do título (tipo/gravidade + data).
  sobretitulo?: ReactNode;
  apoio?: ReactNode;
  // Terceira linha, mais clara.
  meta?: ReactNode;
  // Coluna da direita (valor, horário, status).
  fim?: ReactNode;
  // Ação que não abre a linha (menu de três pontos) — fica fora da área tocável.
  acao?: ReactNode;
  // Linha inteira tocável. Sem onClick, a linha é só leitura.
  onClick?: () => void;
  // Mostra a seta à direita (linha que abre um detalhe).
  seta?: boolean;
  // Título e apoio em até 2 linhas (mensagens de erro, notificações).
  quebrar?: boolean;
  className?: string;
}

export function ItemLista({
  inicio,
  titulo,
  tituloExtra,
  sobretitulo,
  apoio,
  meta,
  fim,
  acao,
  onClick,
  seta,
  quebrar,
  className,
}: ItemListaProps) {
  const conteudo = (
    <>
      {inicio && <span className="shrink-0">{inicio}</span>}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        {sobretitulo && (
          <span className="flex min-w-0 items-center gap-1.5 text-xs">{sobretitulo}</span>
        )}
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={cn(
              'min-w-0 text-[15px] font-semibold leading-snug text-text-title',
              quebrar ? 'line-clamp-2' : 'truncate'
            )}
          >
            {titulo}
          </span>
          {tituloExtra}
        </span>
        {apoio && (
          <span
            className={cn(
              'min-w-0 text-[13px] leading-snug text-text-secondary',
              quebrar ? 'line-clamp-2' : 'truncate'
            )}
          >
            {apoio}
          </span>
        )}
        {meta && (
          <span className="flex min-w-0 items-center gap-1.5 truncate text-xs text-text-hint">
            {meta}
          </span>
        )}
      </span>
      {fim && (
        <span className="flex shrink-0 flex-col items-end gap-1 text-right text-xs text-text-secondary">
          {fim}
        </span>
      )}
      {seta && <ChevronRight size={16} className="shrink-0 text-text-hint" aria-hidden="true" />}
    </>
  );

  const base = 'flex min-h-16 w-full min-w-0 items-center gap-3 px-4 py-3 text-left';

  return (
    <li className={cn('flex items-center', className)}>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className={cn(base, 'transition-colors active:bg-muted/60')}
        >
          {conteudo}
        </button>
      ) : (
        <div className={base}>{conteudo}</div>
      )}
      {acao && <span className="shrink-0 pr-2">{acao}</span>}
    </li>
  );
}

// Selo de status em pílula (Ativo, Bloqueado, Nova...).
export function Selo({
  children,
  tom = 'neutro',
  className,
}: {
  children: ReactNode;
  tom?: 'sucesso' | 'erro' | 'aviso' | 'info' | 'neutro' | 'destaque';
  className?: string;
}) {
  const cores = {
    sucesso: 'bg-success/15 text-success',
    erro: 'bg-error/15 text-error',
    aviso: 'bg-warning/15 text-warning',
    info: 'bg-primary/15 text-primary',
    destaque: 'bg-tertiary/15 text-tertiary',
    neutro: 'bg-muted text-text-secondary',
  } as const;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold',
        cores[tom],
        className
      )}
    >
      {children}
    </span>
  );
}

// Avatar redondo com iniciais e, opcionalmente, bolinha de online.
export function AvatarIniciais({
  iniciais,
  className,
  online,
}: {
  iniciais: string;
  className?: string;
  online?: boolean;
}) {
  return (
    <span
      className={cn(
        'relative flex size-11 items-center justify-center rounded-full text-sm font-bold',
        className
      )}
    >
      {iniciais}
      {online !== undefined && (
        <span
          className={cn(
            'absolute -bottom-0.5 -right-0.5 size-3 rounded-full ring-2 ring-card',
            online ? 'bg-online' : 'bg-offline'
          )}
          aria-label={online ? 'Online' : 'Offline'}
        />
      )}
    </span>
  );
}

// Ícone num quadrado arredondado colorido (tipo de notificação, gravidade...).
export function IconeQuadrado({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'flex size-10 items-center justify-center rounded-xl bg-muted text-text-secondary',
        className
      )}
    >
      {children}
    </span>
  );
}

// Bloco de números lado a lado (2×2 ou 1×3) com fio entre eles.
export function GradeNumeros({
  itens,
  colunas = 3,
  className,
}: {
  itens: { rotulo: string; valor: ReactNode; cor?: string; extra?: ReactNode }[];
  colunas?: 2 | 3;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'grid overflow-hidden rounded-2xl border border-border bg-card shadow-card',
        colunas === 2 ? 'grid-cols-2' : 'grid-cols-3',
        className
      )}
    >
      {itens.map((item, i) => (
        <div
          key={item.rotulo}
          className={cn(
            'flex min-w-0 flex-col gap-0.5 px-3.5 py-3',
            (i + 1) % colunas !== 0 && 'border-r border-border/70',
            i >= colunas && 'border-t border-border/70'
          )}
        >
          <span className={cn('text-xl font-bold tabular-nums text-text-title', item.cor)}>
            {item.valor}
            {item.extra}
          </span>
          <span className="truncate text-[11px] text-text-secondary">{item.rotulo}</span>
        </div>
      ))}
    </div>
  );
}
